import { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import {
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { CancellationReason, ConfigurationStatus } from '@prisma/client';
import { Formik } from 'formik';

import { MetaTags, useQuery } from '@redwoodjs/web';

import { CustomInput } from 'src/components/CustomInput';
import { ConfirmationModal } from 'src/components/Modals';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { CANCELLATION_REASONS_TABLE_HEADERS } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import {
    CREATE_CANCELLATION_REASON_MUTATION,
    DELETE_CANCELLATION_REASON_MUTATION,
} from 'src/graphql/mutations/cancellationReasons.mutation';
import { GET_CANCELLATION_REASONS } from 'src/graphql/queries/cancellationReasons.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import './CancellationReasonsPageStyles.css';
import EditCancellationReasonModalContent from './EditCancellationReasonModalContent';

const CancellationReasonsPage = () => {
    const { isAdmin, user } = useAuthContext();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openCancellationReasonModal, setOpenCancellationModal] = useState(false);
    const [id, setId] = useState<number | null>();
    const [status, setStatus] = useState<ConfigurationStatus>('ACTIVE');
    const [selectedReason, setSelectedReason] = useState<CancellationReason>();

    const [description, setDescription] = useState('');
    const [viewingReason, setViewingReason] = useState<{
        description: string;
        status: string;
    }>({
        description: '',
        status: '',
    });

    const { data, refetch } = useQuery(GET_CANCELLATION_REASONS, {
        fetchPolicy: 'cache-and-network',
        onError: () => {
            return showErrorToast('There was an error retrieving cancellation reasons');
        },
    });

    const [deleteCancellationReason] = useMutation(DELETE_CANCELLATION_REASON_MUTATION, {
        onCompleted: () => {
            showSuccessToast('Cancellation Reason Deleted Successfully');
            refetch();
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting the Reason');
        },
    });

    const tableData = useMemo(() => {
        if (data?.cancellationReasons?.length) {
            return data.cancellationReasons.map((reason) => {
                return {
                    ...reason,
                    dateCreated: reason.createdAt,
                    status: <StatusChip status={reason?.status} type="cancellationReasons" />,
                };
            });
        }
        return [];
    }, [data]);

    const handleOpenDeleteModal = (id, reason) => {
        setId(id);
        setViewingReason({
            description: reason.description,
            status: reason.status.props.status,
        });
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    // Function used to delete the specified cancellation reason
    const handleDeleteRecord = () => {
        // GraphQL mutation
        deleteCancellationReason({ variables: { id } });
        handleCloseDeleteModal();
    };

    const handleCloseCancellationReasonModal = () => {
        setOpenCancellationModal(false);
    };
    const handleAddCancellationReason = () => {
        // Open add reason modal
        setOpenCancellationModal(true);
    };

    const handleViewCancellationReason = ({ id }) => {
        console.log('----', id);
    };

    const [createCancellationReasonMutation] = useMutation(CREATE_CANCELLATION_REASON_MUTATION, {
        onCompleted: () => {
            handleCloseCancellationReasonModal();
            showSuccessToast('Cancellation Reason Created Successfully');
            refetch();
        },
        onError: () => {
            showErrorToast('Error Creating Cancellation Reason');
        },
        variables: {
            input: {
                status,
                description,
                createdBy: user.id,
                lastModifiedBy: user.id,
            },
        },
    });

    const onCloseEditCancellationReasonModal = () => {
        setSelectedReason(null);
    };

    const onConfirmCancellationReasonModal = async () => {
        await refetch();
        onCloseEditCancellationReasonModal();
    };

    return (
        <PageContent
            heading="Cancellation Reasons"
            subHeading="Add, view and remove cancellation reasons"
        >
            <MetaTags title="Cancellation Reasons" description="Cancellation Reasons Page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    <TableList
                        addRecordButtonText="Add Cancellation Reason"
                        data={tableData}
                        headers={CANCELLATION_REASONS_TABLE_HEADERS}
                        title="Cancellation Reasons List"
                        addRecordCallBack={handleAddCancellationReason}
                        handleViewRecord={handleViewCancellationReason}
                        deleteRecordCallBack={handleOpenDeleteModal}
                        enableExport
                        enableEdit={isAdmin}
                        handleEditRecord={(reason) => {
                            const selectedReasonId: number = (reason as CancellationReason).id;
                            setSelectedReason(
                                data?.cancellationReasons.find(({ id }) => {
                                    return id === selectedReasonId;
                                })
                            );
                        }}
                        filterOptions={{}}
                    />

                    <ConfirmationModal
                        isOpen={openDeleteModal}
                        title="Delete Cancellation Reason"
                        justifyButtonContent="end"
                        cancelBtnVariant="back-button"
                        dialogActionMarginTop={'14px'}
                        message={
                            <>
                                <Grid
                                    item
                                    xs={12}
                                    lg={12}
                                    width={800}
                                    container
                                    direction="row"
                                    alignItems="center"
                                    gap={4}
                                    marginBottom={3}
                                >
                                    <Grid item>
                                        <Typography
                                            sx={{
                                                color: '#000',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Are you sure you want to delete this Cancellation
                                            Reason?
                                        </Typography>
                                    </Grid>
                                    <Grid item container direction={'row'} gap={3}>
                                        <Grid item>
                                            <Typography
                                                sx={{
                                                    color: '#000',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Status
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                sx={{
                                                    color: '#000',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {viewingReason?.status}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container direction={'column'} item xs={12} md={12} gap={3}>
                                    <Grid item>
                                        <Typography
                                            sx={{
                                                color: '#000',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Cancellation Reason
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            sx={{
                                                color: '#000',
                                                fontWeight: 400,
                                            }}
                                        >
                                            {viewingReason?.description}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </>
                        }
                        onCancel={handleCloseDeleteModal}
                        onConfirm={() => {
                            handleDeleteRecord();
                            handleCloseDeleteModal();
                        }}
                    />
                    <ConfirmationModal
                        isOpen={openCancellationReasonModal}
                        title="Add Cancellation Reason"
                        justifyButtonContent="end"
                        cancelBtnVariant="back-button"
                        dialogActionMarginTop={'14px'}
                        message={
                            <Formik>
                                <>
                                    <Grid
                                        item
                                        xs={12}
                                        lg={12}
                                        width={800}
                                        container
                                        direction="row"
                                        alignItems="center"
                                        gap="16px"
                                        marginBottom={3}
                                    >
                                        <Grid item>
                                            <FormLabel
                                                id="cancellation-reason-status"
                                                sx={{
                                                    color: '#000',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Status
                                            </FormLabel>
                                        </Grid>
                                        <Grid item>
                                            <FormControl>
                                                <RadioGroup
                                                    aria-labelledby="radio-buttons"
                                                    defaultValue="ACTIVE"
                                                    name="status-group"
                                                    row
                                                    onChange={(event) => {
                                                        setStatus(event.target.value);
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="ACTIVE"
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                sx={{
                                                                    '&.Mui-checked': {
                                                                        '& .MuiSvgIcon-root': {
                                                                            borderRadius: '50%',
                                                                            border: '6px solid #F18F34',
                                                                            backgroundColor:
                                                                                'white',
                                                                            color: 'white',
                                                                        },
                                                                    },

                                                                    '& .MuiSvgIcon-root': {
                                                                        color: 'white',
                                                                        borderRadius: '50%',
                                                                        border: '1px solid #F18F34',
                                                                        backgroundColor: 'white',
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label="ACTIVE"
                                                    />
                                                    <FormControlLabel
                                                        value="INACTIVE"
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                sx={{
                                                                    '&.Mui-checked': {
                                                                        '& .MuiSvgIcon-root': {
                                                                            borderRadius: '50%',
                                                                            border: '6px solid #F18F34',
                                                                            backgroundColor:
                                                                                'white',
                                                                            color: 'white',
                                                                            marginLeft: '1rem',
                                                                        },
                                                                    },

                                                                    '& .MuiSvgIcon-root': {
                                                                        color: 'white',
                                                                        marginLeft: '1rem',
                                                                        borderRadius: '50%',
                                                                        border: '1px solid #F18F34',
                                                                        backgroundColor: 'white',
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label="INACTIVE"
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <CustomInput
                                            name="Cancellation Reason"
                                            textFieldBorderRadius="4px"
                                            textFieldPadding="31.5px 14px"
                                            alignInput="top"
                                            label="Cancellation Reason"
                                            size="large"
                                            maxLength={50}
                                            onChange={(e) => {
                                                return setDescription(e.target.value);
                                            }}
                                        />
                                    </Grid>
                                </>
                            </Formik>
                        }
                        onCancel={handleCloseCancellationReasonModal}
                        onConfirm={() => {
                            createCancellationReasonMutation();
                            handleCloseCancellationReasonModal();
                        }}
                    />
                    <ConfirmationModal
                        isOpen={selectedReason?.description?.length > 0}
                        modalWidth="30%"
                        onCancel={onCloseEditCancellationReasonModal}
                        title="Edit Suspend Reason"
                        hideCancelButton
                        message={
                            <EditCancellationReasonModalContent
                                selectedReasonId={selectedReason?.id}
                                description={selectedReason?.description}
                                status={selectedReason?.status}
                                onCancel={onCloseEditCancellationReasonModal}
                                onConfirm={onConfirmCancellationReasonModal}
                            />
                        }
                    />
                </Grid>
            </Grid>
        </PageContent>
    );
};

export default CancellationReasonsPage;
